// api.js - For integrating with Django backend
import { getCountryCallingCode } from 'react-phone-number-input/input';

export const API_URL = process.env.NODE_ENV === 'production'
  ? 'api'
  : `http://localhost:8000/api`;

/**
 * Check if an email already exists in the system
 * @param {string} email - The email to check
 * @returns {Promise<boolean>} - Whether the email exists
 */

export const checkEmailExists = async (email) => {
  try {
    const response = await fetch(`${API_URL}/tenants/check-email/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
    
    const data = await response.json();
    return data.exists;
  } catch (error) {
    console.error('Error checking email:', error);
    return false;
  }
};

/**
 * Submit user profile data (first step)
 * @param {Object} userData - The user profile data
 * @returns {Promise<Object>} - Response from the API
 */
export const submitUserProfile = async (userData) => {
  try {
    const response = await fetch(`${API_URL}/tenants/profile-setup/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        first_name: userData.firstName,
        last_name: userData.lastName,
        email: userData.email,
        phone: userData.phone,
        role: userData.role,
        wa_plan: userData.waPlan,
        channel: userData.channel,
        country_Code: getCountryCallingCode(userData.countryCode),
        promo_code: userData.promoCode
      }),
    });
    
    return await response.json();
  } catch (error) {
    console.error('Error submitting user profile:', error);
    return { success: false, error: error.message };
  }
};

// A gateway timeout (504) returns an HTML page, so the body can't always be parsed as JSON
const readJsonSafely = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

/**
 * Submit company details and start provisioning the account.
 * The tenant build can outlast the gateway timeout; the onboarding status endpoint is the source of truth.
 * @param {Object} companyData - The company data
 * @param {number} leadId - The ID of the lead created in the first step
 * @returns {Promise<Object>} - { success, status, data, error }; status is 0 on a network error
 */
export const submitCompanyDetails = async (companyData, leadId) => {
  try {
    const response = await fetch(`${API_URL}/tenants/company-details/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        company_name: companyData.companyName,
        industry: companyData.industry,
        company_size: companyData.size,
        website: companyData.website,
        country: companyData.country,
        state: companyData.state || null,
        gst: companyData.gst,
        primary_focus: companyData.primaryFocus,
        hear_about_us: companyData.hearAboutUs,
        lead_id: leadId
      }),
    });

    const data = await readJsonSafely(response);
    return {
      success: response.ok && data.success !== false,
      status: response.status,
      data,
      error: data.message || data.error
    };
  } catch (error) {
    console.error('Error submitting company details:', error);
    return { success: false, status: 0, data: {}, error: error.message };
  }
};

/**
 * Finalize onboarding once provisioning is done.
 * @returns {Promise<Object>} - { success, status, data, error }; status 409 means setup is not done yet, 0 a network error
 */
export const finalizeOnboarding = async (leadId) => {
  try {
    const response = await fetch(`${API_URL}/tenants/onboarding/finalize/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ leadId }),
    });

    const data = await readJsonSafely(response);

    if (response.status === 200) {
      // Return the complete response for processing in the component
      return {
        success: true,
        status: response.status,
        data
      };
    } else {
      return {
        success: false,
        status: response.status,
        data,
        error: data.msg || 'Failed to finalize onboarding'
      };
    }
  } catch (error) {
    console.error('Error finalizing onboarding:', error);
    return {
      success: false,
      status: 0,
      data: {},
      error: 'Network error occurred while finalizing account setup'
    };
  }
};

/**
 * Poll the onboarding status of a lead.
 * @returns {Promise<Object>} - { success, status, data, error }; data has stage, provisioning_status, failed_step and error
 */
export const getOnboardingStatus = async (leadId) => {
  try {
    const response = await fetch(`${API_URL}/tenants/onboarding/status/${leadId}/`, {
      method: 'GET'
    });

    const data = await readJsonSafely(response);

    if (response.status === 200) {
      // Return the complete response for processing in the component
      return {
        success: true,
        status: response.status,
        data
      };
    } else {
      return {
        success: false,
        status: response.status,
        data,
        error: data.msg || 'Failed to get onboarding status'
      };
    }
  } catch (error) {
    console.error('Error getting onboarding status:', error);
    return {
      success: false,
      status: 0,
      data: {},
      error: 'Network error occurred while getting onboarding status'
    };
  }
};